import type { TFunction } from "i18next";

import { formatNumberBasedOnUser, valueOrAlternative } from "utils/namesManipulation";
import { kwh, m2 } from "utils/textSymbols";

import type {
  EpcEnergyConsumption,
  EpcEnevRequirements,
  EpcExtractionData,
  EpcHeatingConsumptionRecord,
  EpcUnitValue,
} from "../components/address-step/epc-modal/types";

import {
  extractSelectedCheckboxLabels,
  formatCheckboxOptionStatuses,
  formatEpcDisplayValue,
  formatYesNo,
  formatYesNoWithFallback,
} from "./epcFieldFormatters";
import { getEpcAutofillValues } from "./epcSuggestedFields";

export type EpcPreviewRow = {
  isNumeric?: boolean;
  label: string;
  value: number | string;
  variant?: "groupHeader" | "nested" | "text";
};

export type EpcPreviewSection = {
  id: string;
  rows: EpcPreviewRow[];
  title: string;
};

const HEATING_CONSUMPTION_RECORD_FIELDS: ReadonlyArray<keyof EpcHeatingConsumptionRecord> = [
  "from",
  "to",
  "energySource",
  "energyConsumptionKwh",
  "heatingShareKwh",
  "hotWaterShareKwh",
  "climateFactor",
  "primaryEnergyFactor",
] as const;

const ENEV_REQUIREMENT_FIELDS: (keyof EpcEnevRequirements)[] = [
  "buildingEnvelopeHtActual",
  "buildingEnvelopeHtRequirement",
  "primaryEnergyDemandActual",
  "primaryEnergyDemandRequirement",
];

const ENERGY_CONSUMPTION_FIELDS: (keyof EpcEnergyConsumption)[] = [
  "finalEnergyConsumption",
  "finalEnergyConsumptionForPropertyAdvertisements",
  "primaryEnergyConsumption",
];

const formatUnitValue = (entry: EpcUnitValue): string => {
  return entry.unit ? `${entry.value} ${entry.unit}` : String(entry.value);
};

const formatList = (items: undefined | unknown[]): string => {
  if (!items?.length) return valueOrAlternative("");
  return items.map((item) => formatEpcDisplayValue(item)).join(", ");
};

const pushRow = (rows: EpcPreviewRow[], label: string, value: unknown, isNumeric?: boolean) => {
  const fallback = valueOrAlternative("");
  const formatted = formatEpcDisplayValue(value, fallback);
  if (!formatted || formatted === fallback) return;
  rows.push({ isNumeric, label, value: formatted });
};

const pushAdditionalRow = (rows: EpcPreviewRow[], label: string, value: unknown) => {
  rows.push({ label, value: formatEpcDisplayValue(value) });
};

const pushUnitRow = (rows: EpcPreviewRow[], label: string, entry: EpcUnitValue | undefined) => {
  if (!entry || entry.value == null) return;
  const formatted = formatUnitValue(entry);
  rows.push({ label, value: formatted });
};

const formatHeatingSystem = (heatingSystem: unknown, t: TFunction): string => {
  const selected = extractSelectedCheckboxLabels(heatingSystem);
  if (selected.length) return selected.join(", ");
  if (heatingSystem) return t(`AddBuilding.buildingDetailsStep.heatingOptions.${heatingSystem}`);
  return formatEpcDisplayValue(heatingSystem);
};

const buildEnergyPerformanceRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const { page2 } = epcData;
  const rows: EpcPreviewRow[] = [];

  pushUnitRow(rows, t("AddBuilding.epcModal.preview.primaryEnergyDemand"), page2?.energyDemand?.primaryEnergyDemand);
  pushUnitRow(rows, t("AddBuilding.epcModal.preview.finalEnergyDemand"), page2?.energyDemand?.finalEnergyDemand);
  pushUnitRow(rows, t("AddBuilding.epcModal.preview.co2Emissions"), page2?.energyDemand?.co2Emissions);

  ENEV_REQUIREMENT_FIELDS.forEach((key) => {
    pushUnitRow(
      rows,
      t(`AddBuilding.epcModal.preview.enev.${key}`, { defaultValue: key }),
      page2?.enevRequirements?.[key]
    );
  });

  return rows;
};

const buildBuildingDetailsRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const building = epcData.page1?.building;
  const rows: EpcPreviewRow[] = [];

  pushAdditionalRow(rows, t("AddBuilding.epcModal.preview.buildingPart"), building?.buildingPart);
  pushAdditionalRow(
    rows,
    t("AddBuilding.epcModal.preview.livingAreaRegulation"),
    formatYesNo(building?.livingAreaAccordingToRegulationChecked)
  );
  pushAdditionalRow(rows, t("AddBuilding.epcModal.preview.reasonForIssue"), building?.reasonForIssue);
  pushAdditionalRow(rows, t("AddBuilding.epcModal.preview.ventilationCooling"), building?.ventilationCooling);
  pushAdditionalRow(rows, t("AddBuilding.epcModal.preview.renewableEnergyType"), building?.renewableEnergyType);
  pushAdditionalRow(rows, t("AddBuilding.epcModal.preview.renewableEnergyUsage"), building?.renewableEnergyUsage);

  return rows;
};

const buildCertificateRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const issuer = epcData.page1?.issuer;
  const quality = epcData.page1?.qualityInformation;
  const rows: EpcPreviewRow[] = [];

  pushRow(rows, t("AddBuilding.epcModal.preview.issueDate"), issuer?.issueDate);
  pushRow(rows, t("AddBuilding.epcModal.preview.issuer"), formatList(issuer?.lines));
  pushRow(rows, t("AddBuilding.epcModal.preview.uploadedFilename"), epcData.uploadedFilename);

  pushRow(
    rows,
    t("AddBuilding.epcModal.preview.energyDemandCertificate"),
    formatYesNo(quality?.energyDemandCertificate)
  );
  pushRow(
    rows,
    t("AddBuilding.epcModal.preview.energyConsumptionCertificate"),
    formatYesNo(quality?.energyConsumptionCertificate)
  );
  pushRow(
    rows,
    t("AddBuilding.epcModal.preview.dataCollectedByAssessor"),
    formatYesNo(quality?.dataCollectedByAssessor)
  );
  pushRow(rows, t("AddBuilding.epcModal.preview.dataCollectedFromOwner"), formatYesNo(quality?.dataCollectedFromOwner));
  pushRow(
    rows,
    t("AddBuilding.epcModal.preview.additionalInformationVoluntary"),
    formatYesNo(quality?.additionalInformationVoluntary)
  );

  return rows;
};

const formatConsumptionRecordValue = (
  key: (typeof HEATING_CONSUMPTION_RECORD_FIELDS)[number],
  value: EpcHeatingConsumptionRecord[(typeof HEATING_CONSUMPTION_RECORD_FIELDS)[number]]
): string => {
  if (value == null || value === "") return valueOrAlternative("");
  if (typeof value === "number" && key.toLowerCase().includes(kwh.toLowerCase())) {
    return `${formatNumberBasedOnUser(value)} ${kwh}`;
  }
  return formatEpcDisplayValue(value);
};

const buildHeatingConsumptionRecordRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const rows: EpcPreviewRow[] = [];

  epcData.page3?.heatingAndHotWaterConsumptionRecords?.forEach((record, index) => {
    const from = record.from ?? "";
    const to = record.to ?? "";
    const period =
      from && to ? `${from} – ${to}` : `${t("AddBuilding.epcModal.preview.heatingConsumptionRecords")} ${index + 1}`;

    rows.push({ label: period, value: "", variant: "groupHeader" });
    HEATING_CONSUMPTION_RECORD_FIELDS.forEach((field) => {
      rows.push({
        label: t(`AddBuilding.epcModal.preview.heatingConsumptionRecord.${field}`),
        value: formatConsumptionRecordValue(field, record[field]),
        variant: "nested",
      });
    });
  });

  return rows;
};

const buildConsumptionRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const { energyConsumption } = epcData.page3 ?? {};
  const rows: EpcPreviewRow[] = [];

  ENERGY_CONSUMPTION_FIELDS.forEach((key) => {
    pushUnitRow(
      rows,
      t(`AddBuilding.epcModal.preview.energyConsumption.${key}`, { defaultValue: key }),
      energyConsumption?.[key]
    );
  });

  return rows;
};

const buildProcedureCheckboxRows = (epcData: EpcExtractionData): EpcPreviewRow[] => {
  const { procedureCheckboxes } = epcData.page2 ?? {};
  if (!procedureCheckboxes?.length) return [];

  return procedureCheckboxes
    .filter((item) => item.label)
    .map((item) => ({
      label: item.label,
      value: formatYesNoWithFallback(item.checked, valueOrAlternative("")),
    }));
};

const buildSubstituteMeasuresRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const substituteMeasuresOther = epcData.page2?.substituteMeasuresOther;
  const rows: EpcPreviewRow[] = [];
  pushRow(
    rows,
    t("AddBuilding.epcModal.preview.substituteMeasuresInfo"),
    substituteMeasuresOther?.additionalInformation
  );

  const measures = substituteMeasuresOther?.substituteMeasures;
  const checkboxSummary = formatCheckboxOptionStatuses(measures?.checkboxes);
  if (checkboxSummary) {
    rows.push({
      label: t("AddBuilding.epcModal.preview.substituteMeasures"),
      value: checkboxSummary,
    });
  }
  pushRow(rows, t("AddBuilding.epcModal.preview.page2.enevTightenedPercent"), measures?.enevTightenedPercent);
  pushUnitRow(
    rows,
    t("AddBuilding.epcModal.preview.page2.tightenedBuildingEnvelopeHt"),
    measures?.tightenedBuildingEnvelopeHt
  );
  pushUnitRow(
    rows,
    t("AddBuilding.epcModal.preview.page2.tightenedPrimaryEnergyDemand"),
    measures?.tightenedPrimaryEnergyDemand
  );

  return rows;
};

const buildPage2ExtraRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const { page2 } = epcData;
  const rows: EpcPreviewRow[] = [...buildProcedureCheckboxRows(epcData), ...buildSubstituteMeasuresRows(epcData, t)];

  if (page2?.finalEnergyDemandTable?.length) {
    rows.push({
      label: t("AddBuilding.epcModal.preview.finalEnergyDemandTable"),
      value: formatList(page2.finalEnergyDemandTable),
    });
  }

  return rows;
};

const formatPrefillValue = (value: unknown): string => formatEpcDisplayValue(value);

export const buildEpcPrefillRows = (epcData: EpcExtractionData, t: TFunction): EpcPreviewRow[] => {
  const building = epcData.page1?.building;
  const autofill = getEpcAutofillValues(epcData);
  const heating = formatHeatingSystem(autofill.heating_system, t);
  const buildingTypeDisplay =
    building?.buildingTypeText ?? epcData.suggestedFields?.buildingTypeText ?? formatPrefillValue(undefined);
  const footprintArea = autofill.footprintArea ?? building?.footprintArea;

  return [
    {
      label: t("AddBuilding.epcModal.preview.streetAndNumber"),
      value: formatPrefillValue(autofill.addressInfo.address),
    },
    {
      label: t("AddBuilding.addressStep.fields.postal_code.label"),
      value: formatPrefillValue(autofill.addressInfo.postal_code),
    },
    {
      label: t("AddBuilding.addressStep.fields.city.label"),
      value: formatPrefillValue(autofill.addressInfo.city),
    },
    {
      label: t("AddBuilding.addressStep.fields.country.label"),
      value: formatPrefillValue(autofill.addressInfo.country),
    },
    {
      isNumeric: true,
      label: t("AddBuilding.summaryStep.fields.constructionYear.label"),
      value: valueOrAlternative(autofill.constructionYear ?? building?.constructionYear),
    },
    {
      isNumeric: true,
      label: t("AddBuilding.summaryStep.fields.floors.label"),
      value: valueOrAlternative(""),
    },
    {
      isNumeric: true,
      label: t("AddBuilding.summaryStep.fields.rentalUnits.label"),
      value: valueOrAlternative(autofill.rentalUnits ?? building?.rentalUnits),
    },
    {
      label: t("AddBuilding.summaryStep.buildingTypeLabel"),
      value: formatPrefillValue(buildingTypeDisplay),
    },
    {
      label: t("AddBuilding.epcModal.preview.footprintArea"),
      value: valueOrAlternative(footprintArea, undefined, m2),
    },
    {
      label: t("AddBuilding.buildingDetailsStep.heatingSystemLabel"),
      value: heating,
    },
    {
      isNumeric: true,
      label: t("AddBuilding.buildingDetailsStep.installationDateLabel"),
      value: valueOrAlternative(autofill.installation_date),
    },
    // TODO: Add cooling and PV system rows
    // {
    //   label: t("AddBuilding.buildingDetailsStep.coolingTitle"),
    //   value: autofill.has_cooling != null ? formatYesNo(autofill.has_cooling) : "—",
    // },
    // {
    //   label: t("AddBuilding.buildingDetailsStep.pvTitle"),
    //   value: autofill.has_pv != null ? formatYesNo(autofill.has_pv) : "—",
    // },
    {
      label: t("AddBuilding.buildingDetailsStep.mechanicalVentilationTitle"),
      value: valueOrAlternative(formatYesNo(autofill.mechanical_ventilation)),
    },
  ];
};

export const buildEpcAdditionalSections = (
  epcData: EpcExtractionData | undefined,
  t: TFunction
): EpcPreviewSection[] => {
  if (!epcData) return [];

  const sections: EpcPreviewSection[] = [
    {
      id: "energyPerformance",
      rows: buildEnergyPerformanceRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.energyPerformance"),
    },
    {
      id: "buildingDetails",
      rows: buildBuildingDetailsRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.buildingDetails"),
    },
    {
      id: "consumption",
      rows: buildConsumptionRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.consumption"),
    },
    {
      id: "heatingConsumptionRecords",
      rows: buildHeatingConsumptionRecordRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.heatingConsumptionRecords"),
    },
    {
      id: "certificateDetails",
      rows: buildCertificateRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.certificateDetails"),
    },
    {
      id: "additionalPage2",
      rows: buildPage2ExtraRows(epcData, t),
      title: t("AddBuilding.epcModal.preview.sections.additionalData"),
    },
  ];

  return sections.filter((section) => section.rows.length > 0);
};
